import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, BookOpen, Users, Star, ArrowRight, Code, Cpu,
  Database, Globe, Layers, CheckCircle, UploadCloud, Plus,
  TrendingUp, Briefcase, Search, Book, FileText, HandHelping,
  Zap, Target, Award, ChevronRight, Sparkles, X as FiX
} from 'lucide-react';
import { useAuth } from './context/AuthContext';
import SEO from './components/SEO';
import Footer from './components/Footer';
import Socialicon from './components/Socialicon';
import Dashboard from './components/User';
import Testimonials from './components/Testimonials';
import Feedback from './components/feedback';
import { BannerAd } from './components/AdSense';
import { db, collection, getDocs, doc, getDoc } from './components/firebase';
import { useTheme } from './context/ThemeContext';
import { getTotalStudyMaterialCount } from './utils/firebaseStats';
import useStudyStats from './hooks/useStudyStats';
import subjectPreview from './assets/Subjectpage.png';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { cn } from './lib/utils';
import { SUBJECTS } from './config/subjects';

/* ── ROTATING WORDS ────────────────────────────────────────────── */
const WORDS = ['Notes', 'PYQs', 'Video Lectures', 'Books', 'Important Topics'];
const RotatingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(x => (x + 1) % WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={i}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="inline-block text-primary"
      >
        {WORDS[i]}
      </motion.span>
    </AnimatePresence>
  );
};

/* ── SECTION DIVIDER ───────────────────────────────────────────── */
const SectionDivider = () => (
  <div className="container max-w-7xl mx-auto px-4 sm:px-6">
    <div className="h-px w-full bg-gradient-to-r from-transparent via-border to-transparent" />
  </div>
);

const NativeSelect = ({ value, onChange, children, placeholder }) => (
  <div className="relative w-full">
    <select
      value={value}
      onChange={onChange}
      className="w-full h-11 rounded-xl border border-border/60 bg-background/60 text-sm px-4 pr-10 cursor-pointer outline-none appearance-none text-foreground focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
  </div>
);

/* ── MAIN ───────────────────────────────────────────────────────── */
const Home = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user, isCompany, isAdmin, loading } = useAuth();
  const [course, setCourse] = useState('');
  const [branch, setBranch] = useState('');
  const [semester, setSemester] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const studyStats = useStudyStats();
  const [statsData, setStatsData] = useState({
    totalUsers: '10K+', totalMaterials: '500+', averageRating: '4.8★', activeProjects: '7'
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'Users'));
        const feedbackSnap = await getDocs(collection(db, 'feedback'));
        let avgRating = 4.8;
        if (feedbackSnap.size > 0) {
          const totalRating = feedbackSnap.docs.reduce((acc, d) => acc + (Number(d.data().rating) || 0), 0);
          avgRating = (totalRating / feedbackSnap.size).toFixed(1);
        }
        const contribSnap = await getDocs(collection(db, 'contributions'));
        const deepMaterialsCount = await getTotalStudyMaterialCount();
        const totalUsers = usersSnap.size;
        setStatsData({
          totalUsers: totalUsers > 1000 ? `${(totalUsers / 1000).toFixed(1)}K+` : `${totalUsers}+`,
          totalMaterials: `${deepMaterialsCount}+`,
          averageRating: `${avgRating}★`,
          activeProjects: contribSnap.size > 0 ? `${contribSnap.size}+` : '12+'
        });
        const statsDoc = await getDoc(doc(db, 'metadata', 'stats'));
        if (statsDoc.exists()) {
          const data = statsDoc.data();
          setStatsData(prev => ({
            ...prev,
            totalUsers: data.totalUsers ? (data.totalUsers > 1000 ? `${(data.totalUsers / 1000).toFixed(1)}K+` : `${data.totalUsers}+`) : prev.totalUsers,
            totalMaterials: data.totalMaterials ? `${data.totalMaterials}+` : prev.totalMaterials,
            averageRating: data.averageRating ? `${data.averageRating}★` : prev.averageRating,
            activeProjects: data.activeProjects || prev.activeProjects
          }));
        }
      } catch (e) { console.error(e); }
    };
    fetchStats();
  }, []);

  if (loading) return null;

  const semestersByBranch = {
    cse: ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th'],
    aiml: ['1st', '2nd', '3rd', '4th', '5th'],
    aids: ['1st', '2nd', '3rd', '4th', '5th'],
    it: ['1st', '2nd', '3rd', '4th', '5th'],
  };

  const handleExplore = () => {
    if (course && branch && semester) navigate(`/${course}/${branch}/${semester}`);
    else alert('Please select all fields first.');
  };

  const searchResults = searchQuery.trim()
    ? SUBJECTS.filter(s =>
      s.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.abbr && s.abbr.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    : [];

  const branches = [
    { icon: <Code size={20} />, title: 'Computer Science', desc: 'DSA, OS, DBMS, Networks & more', tag: 'CSE', to: '/btech/cse/1st', color: 'from-violet-500/20 to-violet-500/5', border: 'hover:border-violet-500/40', accent: 'text-violet-400' },
    { icon: <Cpu size={20} />, title: 'AI & Machine Learning', desc: 'ML, Deep Learning, NLP, Vision', tag: 'AIML', to: '/btech/aiml/2nd', color: 'from-cyan-500/20 to-cyan-500/5', border: 'hover:border-cyan-500/40', accent: 'text-cyan-400' },
    { icon: <Database size={20} />, title: 'AI & Data Science', desc: 'Data Mining, Statistics, Big Data', tag: 'AIDS', to: '/btech/aids/2nd', color: 'from-emerald-500/20 to-emerald-500/5', border: 'hover:border-emerald-500/40', accent: 'text-emerald-400' },
    { icon: <Globe size={20} />, title: 'Information Technology', desc: 'Networks, Web, Cloud, Security', tag: 'IT', to: '/btech/it/1st', color: 'from-amber-500/20 to-amber-500/5', border: 'hover:border-amber-500/40', accent: 'text-amber-400' },
  ];

  const materialStats = [
    { icon: <Book size={18} />, label: 'Notes', value: studyStats.notes, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    { icon: <Layers size={18} />, label: 'PYQs', value: studyStats.pyqs, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    { icon: <BookOpen size={18} />, label: 'Books', value: studyStats.books, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: <Code size={18} />, label: 'Videos', value: studyStats.videos, color: 'text-rose-400', bg: 'bg-rose-500/10' },
    { icon: <CheckCircle size={18} />, label: 'Practicals', value: studyStats.practicals, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  ];

  const topStats = [
    { icon: <Book size={20} />, value: statsData.totalMaterials, label: 'Resources', color: 'text-primary', bg: 'bg-primary/10' },
    { icon: <Users size={20} />, value: statsData.totalUsers, label: 'Students', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    { icon: <Star size={20} />, value: statsData.averageRating, label: 'Rating', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { icon: <UploadCloud size={20} />, value: statsData.activeProjects, label: 'Contributions', color: 'text-rose-400', bg: 'bg-rose-500/10' },
  ];

  if (isCompany) return <CompanyHomeView navigate={navigate} statsData={statsData} />;

  return (
    <div className="bg-background min-h-screen overflow-x-hidden">
      <SEO
        title="SnapED codeCampus | Notes, PYQs, Video Lectures & Courses"
        description="SnapED codeCampus — IPU University's #1 study platform with notes, PYQs, video lectures for B.Tech students."
        keywords={['SnapED codeCampus', 'IPU notes', 'previous year questions', 'video lectures', 'B.Tech study']}
        canonicalUrl="https://snapedcodecampus.tech/"
      />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative min-h-[92vh] flex items-center pt-32 pb-12 overflow-hidden">
        {/* Rich layered background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[130px]" />
          <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[120px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-cyan-500/[0.03] rounded-full blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.15]"
            style={{ backgroundImage: 'radial-gradient(circle, var(--border) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-transparent to-background/80" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/60" />
        </div>

        <div className="container max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* ── LEFT COLUMN ─────────────────────────────────────────── */}
            <motion.div initial={{ opacity: 0, x: -32 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }}>
              {/* Live badge */}
              <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/25 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                <span className="text-[11px] font-black text-primary uppercase tracking-[0.18em]">IPU's #1 Study Platform</span>
              </motion.div>

              {/* Giant heading */}
              <p className="text-xs font-black text-muted-foreground/50 uppercase tracking-[0.35em] mb-2">Welcome to</p>
              <h1 className="text-7xl sm:text-8xl md:text-9xl font-black leading-[0.9] tracking-tighter mb-5">
                <span className="text-foreground">SNAP</span>
                <span className="bg-gradient-to-br from-primary via-primary/90 to-emerald-300 bg-clip-text text-transparent" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ED</span>
              </h1>

              <div className="text-lg sm:text-xl font-semibold text-muted-foreground mb-2 flex items-center gap-2 flex-wrap">
                Master your <span className="text-foreground font-black"><RotatingWord /></span>
              </div>
              <p className="text-sm text-muted-foreground/60 font-mono mb-8 tracking-wide">// Free. Community-driven. Always updated.</p>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3 mb-8">
                <Button onClick={() => navigate('/ipu-result')} size="lg"
                  className="h-13 px-8 rounded-xl bg-primary hover:bg-primary/90 text-black font-black text-sm shadow-2xl shadow-primary/40 transition-all hover:scale-105 hover:shadow-primary/50 border-0">
                  🎓 Check IPU Results
                </Button>
                <Button variant="outline" onClick={() => navigate(user ? '/profile?tab=contribution' : '/login')} size="lg"
                  className="h-13 px-8 rounded-xl border-border/60 hover:border-primary/50 hover:bg-primary/5 font-bold text-sm transition-all hover:scale-105 gap-2 backdrop-blur-sm">
                  <HandHelping className="h-4 w-4 text-primary" /> Contribute
                </Button>
              </div>

              {/* Glassmorphism selector */}
              <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.03] backdrop-blur-2xl shadow-2xl">
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                <div className="p-5">
                  <p className="text-[10px] font-black text-muted-foreground/60 uppercase tracking-[0.2em] mb-4">Quick Study Session</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[9px] font-black text-muted-foreground/50 uppercase tracking-[0.2em] block mb-1.5">Course</label>
                      <NativeSelect value={course} onChange={e => { setCourse(e.target.value); setBranch(''); setSemester(''); }} placeholder="Course">
                        <option value="btech">B.Tech</option>
                      </NativeSelect>
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-muted-foreground/50 uppercase tracking-[0.2em] block mb-1.5">Branch</label>
                      <NativeSelect value={branch} onChange={e => { setBranch(e.target.value); setSemester(''); }} placeholder="Branch">
                        {course && <>
                          <option value="cse">CSE</option>
                          <option value="aiml">AIML</option>
                          <option value="aids">AIDS</option>
                          <option value="it">IT</option>
                        </>}
                      </NativeSelect>
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-muted-foreground/50 uppercase tracking-[0.2em] block mb-1.5">Semester</label>
                      <NativeSelect value={semester} onChange={e => setSemester(e.target.value)} placeholder="Sem">
                        {(semestersByBranch[branch] || []).map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </NativeSelect>
                    </div>
                  </div>
                </div>
                <button onClick={handleExplore} disabled={!(course && branch && semester)}
                  className={cn('w-full h-11 font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all duration-200',
                    course && branch && semester
                      ? 'bg-primary hover:bg-primary/90 text-black cursor-pointer'
                      : 'bg-white/[0.04] text-muted-foreground/40 cursor-not-allowed')}>
                  Initialize Study Session <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>

            {/* ── RIGHT COLUMN ─────────────────────────────────────────── */}
            <motion.div initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.7, ease: 'easeOut' }}>
              <div className="rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-2xl shadow-2xl overflow-hidden">
                {/* Search */}
                <div className="p-5 border-b border-white/[0.06]">
                  <p className="text-[10px] font-black text-muted-foreground/50 uppercase tracking-[0.22em] mb-3">Quick Access</p>
                  <div className="relative">
                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                    <input
                      type="text"
                      placeholder="Search subjects, notes, PYQs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full h-11 pl-10 pr-10 rounded-xl border border-white/[0.08] bg-white/[0.03] text-sm outline-none hover:border-primary/30 hover:bg-primary/5 focus:border-primary/50 transition-all placeholder:text-muted-foreground/40"
                    />
                    {searchQuery ? (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                      >
                        <FiX size={14} />
                      </button>
                    ) : (
                      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground/30 border border-white/10 rounded px-1.5 py-0.5">↵</kbd>
                    )}

                    <AnimatePresence>
                      {searchResults.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 4, scale: 0.98 }}
                          className="absolute top-[calc(100%+8px)] left-0 right-0 bg-[#0f172a] border-2 border-primary/40 rounded-2xl p-2 shadow-[0_20px_50px_rgba(0,0,0,0.8)] z-[100] max-h-[300px] overflow-y-auto"
                        >
                          <div className="px-3 py-2 text-[10px] font-black text-primary uppercase tracking-[0.2em] flex justify-between border-b border-primary/10 mb-1">
                            <span>Results ({searchResults.length})</span>
                          </div>
                          {searchResults.map((result) => (
                            <div
                              key={result.path}
                              onClick={() => {
                                navigate(result.path);
                                setSearchQuery('');
                              }}
                              className="p-3 rounded-xl cursor-pointer hover:bg-primary/10 hover:border-primary/20 border border-transparent transition-all flex flex-col gap-0.5 mb-1 text-left group"
                            >
                              <div className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{result.label}</div>
                              <div className="text-[11px] text-muted-foreground font-medium">{result.sub}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* 2x2 feature cards */}
                <div className="grid grid-cols-2">
                  {[
                    { icon: <BookOpen size={18} />, title: 'Lecture Notes', desc: 'Community-curated subject notes', ig: 'bg-primary/15', ic: 'text-primary', hov: 'hover:bg-primary/5', b: 'border-b border-r border-white/[0.06]', gFrom: '#22c55e20', gTo: '#22c55e05' },
                    { icon: <TrendingUp size={18} />, title: 'Video Lectures', desc: 'Video learning from top educators', ig: 'bg-violet-500/15', ic: 'text-violet-400', hov: 'hover:bg-violet-500/5', b: 'border-b border-white/[0.06]', gFrom: '#8b5cf620', gTo: '#8b5cf605' },
                    { icon: <FileText size={18} />, title: 'PYQs', desc: 'Previous year exam questions', ig: 'bg-cyan-500/15', ic: 'text-cyan-400', hov: 'hover:bg-cyan-500/5', b: 'border-r border-white/[0.06]', gFrom: '#06b6d420', gTo: '#06b6d405' },
                    { icon: <Layers size={18} />, title: 'Books & Refs', desc: 'Reference books & materials', ig: 'bg-amber-500/15', ic: 'text-amber-400', hov: 'hover:bg-amber-500/5', b: '', gFrom: '#f59e0b20', gTo: '#f59e0b05' },
                  ].map((c, i) => (
                    <button key={i} onClick={() => navigate('/btech/cse/1st')}
                      className={cn('p-5 text-left group relative overflow-hidden transition-all duration-300', c.hov, c.b)}>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: `radial-gradient(circle at 20% 50%, ${c.gFrom}, ${c.gTo})` }} />
                      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center mb-3 relative z-10 group-hover:scale-110 transition-transform shadow-sm', c.ig, c.ic)}>
                        {c.icon}
                      </div>
                      <div className="text-xs font-black text-foreground uppercase tracking-widest mb-1 relative z-10">{c.title}</div>
                      <p className="text-[11px] text-muted-foreground/70 leading-snug relative z-10">{c.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Live footer */}
                <div className="px-5 py-3.5 bg-white/[0.02] border-t border-white/[0.06] flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                    </span>
                    <span className="text-xs font-black text-emerald-400">{statsData.totalUsers} Active Students</span>
                  </div>
                  <div className="flex items-end gap-[2px] h-4 opacity-50">
                    {[2, 4, 3, 6, 5, 7, 4, 8, 6, 5, 7, 9, 6, 8].map((h, i) => (
                      <div key={i} className="w-1 rounded-sm bg-primary" style={{ height: `${h * 10}%` }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats strip below panel */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                {[
                  { label: 'Resources', value: statsData.totalMaterials, color: 'text-primary' },
                  { label: 'Rating', value: statsData.averageRating, color: 'text-amber-400' },
                  { label: 'Contributions', value: statsData.activeProjects, color: 'text-cyan-400' },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-sm px-4 py-3 text-center">
                    <div className={cn('text-xl font-black tracking-tight', s.color)}>{s.value}</div>
                    <div className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-widest mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── AD ────────────────────────────────────────────────────────── */}
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <BannerAd />
      </div>

      <div className="py-20">
        <SectionDivider />
      </div>

      {/* ── STATS STRIP ───────────────────────────────────────────────── */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {topStats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <div className="relative group cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl blur-xl" />
                <Card className="relative p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-3xl text-center hover:border-primary/30 transition-all duration-300 shadow-xl hover:shadow-primary/5 group overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl group-hover:bg-primary/10 transition-colors" />
                  <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg relative z-10 group-hover:scale-110 transition-transform duration-300", s.bg, s.color)}>
                    {React.cloneElement(s.icon, { size: 24 })}
                  </div>
                  <div className="text-3xl font-black text-foreground tracking-tight leading-none mb-2 tabular-nums">{s.value}</div>
                  <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.2em]">{s.label}</div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[3px] bg-primary group-hover:w-1/2 transition-all duration-500 rounded-full" />
                </Card>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── STUDY INVENTORY SECTION ───────────────────────────────────── */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="relative rounded-[2.5rem] bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.05] p-8 md:p-12 overflow-hidden shadow-2xl">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_-20%,var(--primary-muted),transparent_70%)] opacity-20 pointer-events-none" />

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                <span className="text-[10px] font-black text-primary uppercase tracking-widest">Global Repository</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tighter leading-tight">
                Study <span className="text-primary italic">Inventory</span>
              </h2>
              <p className="text-muted-foreground/70 mt-3 max-w-md text-sm font-medium">
                Our dynamic content delivery system tracks resources across branches in real-time.
              </p>
            </div>

            <div className="flex items-center gap-4 bg-white/[0.03] border border-white/[0.08] p-4 rounded-2xl backdrop-blur-md">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden">
                    <img src={`https://i.pravatar.cc/100?u=${i}`} alt="user" className="w-full h-full object-cover grayscale" />
                  </div>
                ))}
              </div>
              <div className="text-left">
                <div className="text-xs font-black text-foreground">Community Powered</div>
                <div className="text-[10px] font-bold text-muted-foreground uppercase">{statsData.totalUsers} Members</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 relative z-10">
            {materialStats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, type: 'spring', stiffness: 100 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group"
              >
                <div className="h-full flex flex-col items-center gap-4 p-6 rounded-3xl bg-background/40 border border-white/[0.05] hover:border-primary/20 hover:bg-background/60 transition-all duration-300 relative overflow-hidden">
                  <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500", s.bg)} />

                  <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500 shadow-inner", s.bg, s.color)}>
                    {s.icon}
                  </div>

                  <div className="text-center">
                    <div className="text-3xl font-black text-foreground tracking-tight mb-1 tabular-nums">
                      {studyStats.loading ? (
                        <div className="w-8 h-8 bg-muted animate-pulse rounded mx-auto" />
                      ) : s.value}
                    </div>
                    <div className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.15em]">{s.label}</div>
                  </div>

                  <div className="w-full mt-2 h-1 bg-muted rounded-full overflow-hidden opacity-30">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '70%' }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + (i * 0.1), duration: 1 }}
                      className={cn("h-full", s.bg)}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── BROWSE BY BRANCH ──────────────────────────────────────────── */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-3">Browse by Branch</div>
            <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tighter leading-tight">
              Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Programme</span>
            </h2>
          </motion.div>
          <p className="text-muted-foreground font-medium text-sm max-w-sm md:text-right italic opacity-70">
            Tailored resources for every engineering discipline.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {branches.map((b, i) => (
            <motion.div
              key={b.tag}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -8 }}
              onClick={() => navigate(b.to)}
              className="cursor-pointer group"
            >
              <Card className={cn(
                "h-full p-8 bg-card/40 backdrop-blur-md border-white/[0.05] rounded-[2rem] transition-all duration-500 hover:shadow-2xl hover:border-primary/20 overflow-hidden relative group"
              )}>
                <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-500", b.color)} />
                <div className="relative z-10">
                  <div className={cn("w-14 h-14 rounded-2xl bg-background/80 flex items-center justify-center mb-6 border border-white/10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg", b.accent)}>
                    {React.cloneElement(b.icon, { size: 24 })}
                  </div>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-black text-xl text-foreground tracking-tight leading-tight group-hover:text-primary transition-colors">{b.title}</h3>
                    <ChevronRight size={18} className={cn("mt-1 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1", b.accent)} />
                  </div>
                  <p className="text-xs text-muted-foreground/80 mb-6 leading-relaxed font-medium line-clamp-2">{b.desc}</p>
                  <div className="flex items-center justify-between">
                    <Badge className={cn("text-[10px] font-black uppercase tracking-widest px-3 py-1 border rounded-lg", b.accent, "bg-current/10 border-current/20")} style={{ backgroundColor: 'transparent' }}>
                      {b.tag}
                    </Badge>
                    <div className="text-[10px] font-black text-primary opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-widest">Explore Now</div>
                  </div>
                </div>

                {/* Decorative element */}
                <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/20 transition-all duration-700" />
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── SUBJECT SHOWCASE ──────────────────────────────────────────── */}
      {/* ... (Showcase implementation preserved) ... */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="relative rounded-[3rem] bg-gradient-to-br from-card/60 to-background/20 border border-white/[0.05] overflow-hidden shadow-3xl">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -mr-64 -mt-64 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[100px] -ml-48 -mb-48 pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 relative z-10">
            <div className="p-8 md:p-16 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <Sparkles size={12} className="text-primary" />
                  <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Next-Gen Learning</span>
                </div>

                <h2 className="text-4xl md:text-6xl font-black text-foreground tracking-tighter leading-[1.1] mb-6">
                  Beautifully Crafted<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Subject Pages</span>
                </h2>

                <p className="text-muted-foreground/80 mb-10 leading-relaxed text-base font-medium max-w-lg">
                  Dive deep into your curriculum with a professional interface. Notes, PYQs, and Video Lectures — all organized in one sleek ecosystem built exclusively for IPU students.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { text: 'Structured Modules', icon: <Layers size={18} />, color: 'text-blue-400', bg: 'bg-blue-400/10' },
                    { text: 'Progress Tracking', icon: <CheckCircle size={18} />, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
                    { text: 'Instant Access', icon: <Zap size={18} />, color: 'text-amber-400', bg: 'bg-amber-400/10' },
                    { text: 'Optimized Vision', icon: <Star size={18} />, color: 'text-violet-400', bg: 'bg-violet-400/10' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * i }}
                      className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.05] hover:border-white/10 transition-colors group"
                    >
                      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110", item.bg, item.color)}>
                        {item.icon}
                      </div>
                      <span className="text-sm font-bold text-foreground/90">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="relative lg:p-12 p-6 flex items-center justify-center bg-white/[0.02]">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, rotateY: 20 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, type: 'spring' }}
                className="relative w-full max-w-lg group"
              >
                <div className="absolute inset-0 bg-primary/20 blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-700 rounded-full" />
                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] transform-gpu transition-transform duration-700 group-hover:scale-[1.02]">
                  <img
                    src={subjectPreview}
                    alt="Subject Page Preview"
                    className="w-full h-auto block"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                </div>

                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -top-6 -right-6 p-4 rounded-2xl bg-card/80 backdrop-blur-xl border border-white/10 shadow-2xl hidden md:block"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                      <Zap size={16} />
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-foreground">Fast Loading</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-16">
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-6xl font-black text-foreground tracking-tighter leading-tight mb-4">
              Your Path to <span className="text-primary italic">Excellence</span>
            </h2>
            <p className="text-muted-foreground font-medium max-w-md mx-auto italic opacity-70">Three simple steps to unlock your potential.</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { step: '01', title: 'Pick Your Branch', desc: 'Select your engineering branch and current semester to get started instantly.', icon: <Target size={24} />, color: 'text-primary', pulse: 'primary' },
            { step: '02', title: 'Find Your Subject', desc: 'Browse curated resources tailored precisely to your IPU B.Tech syllabus.', icon: <Search size={24} />, color: 'text-emerald-400', pulse: 'emerald-400' },
            { step: '03', title: 'Start Studying', desc: 'Ace exams with premium notes, PYQs, and expert video lectures.', icon: <Award size={24} />, color: 'text-violet-400', pulse: 'violet-400' },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-[2.5rem]" />
              <Card className="h-full p-8 bg-card/40 backdrop-blur-sm border-white/[0.05] hover:border-primary/30 rounded-[2.5rem] shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden relative">
                <div className="absolute -top-2 right-4 text-[100px] font-black text-foreground/[0.03] italic leading-none pointer-events-none group-hover:text-primary/[0.05] transition-colors tabular-nums">
                  {item.step}
                </div>

                <div className={cn("inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/10 mb-8 relative z-10 transition-transform duration-500 group-hover:scale-110", item.color)}>
                  {item.icon}
                  <div className={cn("absolute inset-0 rounded-2xl animate-pulse blur-md opacity-20", `bg-${item.pulse}`)} />
                </div>

                <h3 className="text-2xl font-black text-foreground mb-4 tracking-tight relative z-10 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground/80 leading-relaxed font-medium text-sm relative z-10">{item.desc}</p>

                <div className="mt-8 flex items-center gap-2 relative z-10">
                  <div className="h-1 flex-1 bg-white/[0.05] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      viewport={{ once: true }}
                      transition={{ delay: 1 + (i * 0.2), duration: 0.8 }}
                      className={cn("h-full", i === 0 ? 'bg-primary' : i === 1 ? 'bg-emerald-400' : 'bg-violet-400')}
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── SOCIAL ────────────────────────────────────────────────────── */}
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <Socialicon />
      </div>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── USER DASHBOARD ────────────────────────────────────────────── */}
      <Dashboard />

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── AD ────────────────────────────────────────────────────────── */}
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <BannerAd />
      </div>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────── */}
      <Testimonials />

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* ── FINAL CTA ─────────────────────────────────────────────────── */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-24 mb-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[3rem] p-12 md:p-24 text-center overflow-hidden shadow-[0_40px_100px_-20px_rgba(var(--primary-rgb),0.3)] bg-[#0f172a] border border-white/10"
        >
          {/* Animated Background Gradients */}
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-emerald-500/20 opacity-40 pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[100px] animate-pulse pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px] animate-pulse pointer-events-none" />

          <div className="relative z-10 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-4xl md:text-7xl font-black text-white mb-8 tracking-tighter leading-none">
                Ready to grow <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/50">together?</span>
              </h2>
              <p className="text-white/70 text-lg md:text-xl mb-12 font-medium leading-relaxed">
                Join thousands of IPU students mastering their curriculum. Start your journey toward excellence today.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => { document.getElementById('feedback-section')?.scrollIntoView({ behavior: 'smooth' }) || navigate('/feedback'); }}
                  className="h-14 px-10 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-black shadow-2xl shadow-primary/40 transition-all hover:scale-105 gap-2 text-base"
                >
                  <Star className="h-5 w-5 fill-current" /> Give Feedback
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/contactus')}
                  className="h-14 px-10 rounded-2xl bg-white/5 hover:bg-white/10 text-white border-white/20 font-black backdrop-blur-md transition-all hover:scale-105 text-base"
                >
                  Contact Us
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

/* ── COMPANY HOME VIEW ──────────────────────────────────────────── */
const CompanyHomeView = ({ navigate, statsData }) => {
  const recruitmentSteps = [
    { title: 'Post a Job', desc: 'Create detailed job or internship listings to attract the best talent.', icon: <Plus size={20} /> },
    { title: 'Search Candidates', desc: 'Use our advanced search to find students by branch, skills, and CGPA.', icon: <Users size={20} /> },
    { title: 'Manage Applications', desc: 'Track and update application statuses from your dedicated dashboard.', icon: <Layers size={20} /> },
    { title: 'Hire & Grow', desc: 'Connect with top engineering students and build your future team.', icon: <CheckCircle size={20} /> },
  ];

  return (
    <div className="bg-background min-h-screen selection:bg-primary/30">
      <SEO
        title="Recruiter Hub | SnapED codeCampus"
        description="Hire top engineering talent from IPU University. Post jobs and search candidates on SnapED codeCampus."
      />

      {/* Hero Section */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 pt-44 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Recruiter Dashboard</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-foreground tracking-tighter leading-[1.05] mb-8">
              Hire the Best <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Engineering Talent</span>
            </h1>
            <p className="text-xl text-muted-foreground/80 max-w-lg mb-10 font-medium leading-relaxed">
              Access a elite pool of qualified IPU B.Tech students. Streamline your recruitment process with SnapED's high-intelligence job portal.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/job-portal/company/dashboard')}
                className="h-14 px-8 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-black shadow-xl shadow-primary/30 transition-all hover:scale-105 gap-2"
              >
                <Plus className="h-5 w-5" /> Post a Job
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/profile')}
                className="h-14 px-8 rounded-2xl font-black gap-2 backdrop-blur-sm border-border/60 hover:bg-accent transition-all hover:scale-105"
              >
                <Users className="h-5 w-5" /> View Profile
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
            <Card className="relative p-10 bg-card/40 backdrop-blur-2xl border-white/[0.05] rounded-[2.5rem] shadow-2xl overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-primary/10 transition-colors" />
              <h3 className="text-2xl font-black text-foreground mb-8 tracking-tight relative z-10">Platform Reach</h3>

              <div className="grid grid-cols-2 gap-8 relative z-10">
                {[
                  { val: statsData.totalUsers, label: 'Active Students', color: 'text-primary', icon: <Users size={16} /> },
                  { val: statsData.totalMaterials, label: 'Resources', color: 'text-emerald-400', icon: <Layers size={16} /> },
                  { val: '4.8★', label: 'User Rating', color: 'text-amber-400', icon: <Star size={16} /> },
                  { val: '100+', label: 'Daily Visits', color: 'text-cyan-400', icon: <Zap size={16} /> },
                ].map((s, i) => (
                  <div key={i} className="space-y-1">
                    <div className={cn("text-4xl font-black tracking-tighter tabular-nums", s.color)}>{s.val}</div>
                    <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase tracking-widest">
                      {s.icon}
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      {/* Guide Section */}
      <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-24 mb-24">
        <div className="text-center mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tighter mb-4">
              Recruiter's <span className="text-primary italic">Success Guide</span>
            </h2>
            <p className="text-muted-foreground font-medium max-w-md mx-auto italic opacity-70">Maximize your recruitment impact on SnapED.</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recruitmentSteps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -8 }}
              className="group"
            >
              <Card className="h-full p-8 bg-card/60 backdrop-blur-md border-white/[0.05] rounded-[2rem] hover:border-primary/30 hover:shadow-2xl transition-all duration-500 relative overflow-hidden">
                <div className="text-7xl font-black text-foreground/[0.04] absolute top-2 right-4 pointer-events-none group-hover:text-primary/[0.06] transition-colors tabular-nums">
                  {i + 1}
                </div>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 border border-primary/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative z-10 shadow-inner">
                  {step.icon}
                </div>
                <h4 className="text-xl font-black text-foreground mb-3 tracking-tight relative z-10 group-hover:text-primary transition-colors">{step.title}</h4>
                <p className="text-sm text-muted-foreground/80 leading-relaxed font-medium relative z-10">{step.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 p-12 rounded-[2.5rem] bg-gradient-to-br from-primary/5 to-emerald-500/5 border border-primary/10 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_120%,var(--primary-muted),transparent_60%)] opacity-20" />
          <div className="relative z-10">
            <h3 className="text-2xl font-black text-foreground mb-3 tracking-tight leading-tight">Need Specialized Help?</h3>
            <p className="text-muted-foreground text-sm font-medium max-w-md mx-auto mb-8 leading-relaxed">Our employer branding ecosystem is dedicated to connecting you with the absolute best talent pool in the region.</p>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/contactus')}
              className="h-14 px-10 rounded-2xl border-primary/20 text-primary hover:bg-primary hover:text-white font-black transition-all hover:scale-105 shadow-xl shadow-primary/5"
            >
              Contact Employer Support
            </Button>
          </div>
        </motion.div>
      </section>

      <div className="py-12">
        <SectionDivider />
      </div>

      <div id="feedback-section" className="border-t border-white/5 bg-white/[0.01] py-24">
        <Feedback isDashboard={true} />
      </div>
      <Footer />
    </div>
  );
};

export default Home;
